import React from "react";
import { useState, useEffect } from "react";
import axios from "axios";
import { ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  fetchDeviceHealth,
  getDevicesByAgentDetails,
} from "../services/apiServices";
import { useProjectContext } from "context/ProjectContext";
import { useCbomContext } from "context/CbomContext";

interface DevicesUsingAgent {
  device_id: string;
  last_scanned_date: string | Date | null;
  last_health_sync_time: string | Date | null;
}

const fields: (keyof DevicesUsingAgent)[] = [
  "device_id",
  "last_scanned_date",
  "last_health_sync_time",
];
const headers: string[] = [
  "Device ID",
  "Last Scanned Date",
  "Last Health Sync Time",
];
const DevicesUsingSensorAgent: React.FC = () => {
  const { selectedAgentData } = useCbomContext();
  const { state } = useProjectContext();
  const [deviceData, setDeviceData] = useState<DevicesUsingAgent[]>([]);
  const [currentItems, setCurrentItems] = useState<DevicesUsingAgent[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredSources, setFilteredSources] = useState<DevicesUsingAgent[]>(
    []
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const totalPages = Math.ceil(filteredSources.length / itemsPerPage);
  const navigate = useNavigate();
  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };
  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const response = await getDevicesByAgentDetails(
          state.currentProjectID,
          selectedAgentData?.sensor_agent_version,
          selectedAgentData?.sensor_agent_type
        );
        setDeviceData(response.data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchDevices();
  }, [localStorage.getItem("authToken"), state.currentProjectID]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = deviceData.filter(
        (e) =>
          e &&
          Object.values(e)
            .filter(
              (value) => typeof value === "string" || typeof value === "number"
            )
            .join(" ")
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
      setFilteredSources(filtered);
    } else {
      setFilteredSources(deviceData); // Show all data if search term is cleared
      // console.log(agentData);
    }
  }, [searchTerm, deviceData]);
  useEffect(() => {
    setCurrentItems(
      Array.isArray(filteredSources)
        ? filteredSources.slice(indexOfFirstItem, indexOfLastItem)
        : []
    );
  }, [filteredSources, currentPage, itemsPerPage]);

  const nextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const prevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };
  console.log(deviceData);
  return (
    <>
      <div className="h-full w-full p-2 bg-white rounded-lg flex flex-col">
        <div className="flex flex-col bg-custom-blue8 border rounded-md p-2 flex-grow min-h-0">
          <div className="overflow-auto flex-grow custom-scrollbar">
            <table
              id="data-table"
              className="min-w-full bg-white shadow border rounded-md"
            >
              <thead className="sticky top-0 z-10 bg-custom-blue0">
                <tr className="bg-custom-blue0 text-sm text-left text-custom-blue7">
                  {headers.map((header, index) => (
                    <th key={index} className="p-2">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems &&
                Array.isArray(currentItems) &&
                currentItems.length > 0 ? (
                  currentItems.map((source, index) => {
                    return (
                      <tr
                        key={source.device_id}
                        className={`${
                          index % 2 === 1 ? "bg-custom-blue0" : ""
                        } hover:bg-custom-blue0 text-sm text-custom-blue7`}
                      >
                        {fields.map((field) => (
                          <td key={field} className="p-2">
                            {source[field] ? String(source[field]) : "N/A"}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={15}
                      className="text-custom-blue7 p-2 text-center"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="flex justify-between items-center mt-2">
            <div className="flex items-center space-x-2">
              <select
                className="border rounded-md p-2 bg-white"
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
              >
                {/* <option value={5}>5</option> */}
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
                <option value={40}>40</option>
                <option value={50}>50</option>
              </select>
              <span className="text-custom-blue7">Results per page</span>
            </div>
            <div className="flex items-center space-x-2 text-custom-blue7">
              <span>
                Displaying {indexOfFirstItem + 1}-
                {Math.min(indexOfLastItem, filteredSources.length)} of{" "}
                {filteredSources.length}
              </span>
              <button
                className="p-2 bg-white rounded-md"
                onClick={prevPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={20} />
              </button>
              <button
                className="p-2 bg-white rounded-md"
                onClick={nextPage}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DevicesUsingSensorAgent;
